// Libs
import { Avatar, Box, Button, Grid2, IconButton, Stack, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
import { BaseAvatarGroup, Loading } from '~/components/common';
// Others
import { getScheduleDetail } from '~/thunks/schedule/scheduleThunk';
import { DEFAULT_NUMBER_ZERO, EMPTY_STRING } from '~/utils/constants/common';
import { BusinessTypeEnum, DateFormatEnum } from '~/utils/enum/common';
import {
  formatDate,
  formatDateByTimeZone,
  formatTitleCase,
  getFullName,
  getInitials,
} from '~/utils/helper/common';
import { ISchedule, IScheduleDetail } from '~/utils/interface/schedule';
import { useAppDispatch } from '~/utils/redux/hooks';
// Styles, images, icons
import { icons } from '~/assets';
import styles from './ScheduleDetailSummary.module.scss';

type Props = {
  data?: ISchedule;
  onShowConfirmDelete?: (scheduleId: string) => void;
  onShowEditSchedule?: (scheduleId: string) => void;
  onShowDetail?: (scheduleId: string) => void;
};

const cx = classNames.bind(styles);

const ScheduleDetailSummary = (props: Props) => {
  //#region Destructuring Props
  const { data, onShowConfirmDelete, onShowEditSchedule, onShowDetail } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/schedule']);
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [scheduleDetails, setScheduleDetails] = useState<IScheduleDetail>();
  const timeZone =
    scheduleDetails?.event?.timezone ||
    scheduleDetails?.job?.timezone ||
    scheduleDetails?.estimate?.timezone ||
    scheduleDetails?.task?.timezone ||
    EMPTY_STRING;
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  const handleGetScheduleDetails = useCallback(
    (scheduleId: string) => {
      setIsLoading(true);

      dispatch(getScheduleDetail(scheduleId))
        .unwrap()
        .then((res) => {
          if (!res?.data) return;

          const data = res?.data;
          setScheduleDetails(data || {});
        })
        .catch((error) => {})
        .finally(() => {
          setIsLoading(false);
        });
    },
    [dispatch]
  );

  //#region Implement Hook
  useEffect(() => {
    if (!data?.id) return;

    handleGetScheduleDetails(data?.id);
  }, [data?.id, handleGetScheduleDetails]);
  //#endregion Implement Hook

  //#region Handle Function

  const handleShowEditSchedule = () => {
    if (!data?.id) return;

    onShowEditSchedule && onShowEditSchedule(data?.id);
  };

  const handleShowDetail = () => {
    if (!data?.id) return;

    onShowDetail && onShowDetail(data?.id);
  };

  const handleShowConfirmDelete = () => {
    if (!data?.id) return;

    onShowConfirmDelete && onShowConfirmDelete(data?.id);
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} id='scheduleDetailSummary' className={cx('container')}>
      {scheduleDetails?.businessType !== BusinessTypeEnum.TASK && (
        <Grid2 container rowSpacing={'24px'} columnSpacing={'24px'}>
          {data?.businessType !== BusinessTypeEnum.EVENT && (
            <>
              <Grid2 size={6}>
                <Stack direction={'row'} alignItems={'center'}>
                  <Typography component='span' className={cx('key')}>
                    {t('schedule_detail_item_label_id')}
                  </Typography>

                  <Typography component='span' className={cx('value')}>
                    {scheduleDetails?.id
                      ? t('common_value_id', { id: scheduleDetails?.id, ns: 'common/base' })
                      : EMPTY_STRING}
                  </Typography>
                </Stack>
              </Grid2>

              <Grid2 size={6}>
                <Stack direction={'row'} alignItems={'center'}>
                  <Typography component='span' className={cx('key')}>
                    {t('schedule_detail_item_label_type')}
                  </Typography>

                  <Typography component='span' className={cx('value')}>
                    {scheduleDetails?.businessType
                      ? formatTitleCase(scheduleDetails?.businessType)
                      : EMPTY_STRING}
                  </Typography>
                </Stack>
              </Grid2>
            </>
          )}

          {data?.businessType === BusinessTypeEnum.EVENT && (
            <>
              <Grid2 size={6}>
                <Stack direction={'row'} alignItems={'center'}>
                  <Typography component='span' className={cx('key')}>
                    {t('schedule_detail_item_label_name')}
                  </Typography>

                  <Typography component='span' className={cx('value')}>
                    {scheduleDetails?.event?.name || EMPTY_STRING}
                  </Typography>
                </Stack>
              </Grid2>

              <Grid2 size={6}>
                <Stack direction={'row'} alignItems={'center'}>
                  <Typography component='span' className={cx('key')}>
                    {t('schedule_detail_item_label_type')}
                  </Typography>

                  <Typography component='span' className={cx('value')}>
                    {scheduleDetails?.businessType
                      ? formatTitleCase(scheduleDetails?.businessType)
                      : EMPTY_STRING}
                  </Typography>
                </Stack>
              </Grid2>
            </>
          )}

          <Grid2 size={6}>
            <Stack direction={'row'} alignItems={'center'}>
              <Typography component='span' className={cx('key')}>
                {t('schedule_detail_item_label_start_date')}
              </Typography>

              <Stack direction={'row'} gap={'8px'}>
                <Typography component='span' className={cx('value')}>
                  {formatDateByTimeZone(
                    scheduleDetails?.startTime,
                    DateFormatEnum.DD_MMM_YYYY_H_MM_A,
                    timeZone
                  )}
                </Typography>
              </Stack>
            </Stack>
          </Grid2>

          <Grid2 size={6}>
            <Stack direction={'row'} alignItems={'center'}>
              <Typography component='span' className={cx('key')}>
                {t('schedule_detail_item_label_end_date')}
              </Typography>

              <Stack direction={'row'} gap={'8px'}>
                <Typography component='span' className={cx('value')}>
                  {formatDateByTimeZone(
                    scheduleDetails?.endTime,
                    DateFormatEnum.DD_MMM_YYYY_H_MM_A,
                    timeZone
                  )}
                </Typography>
              </Stack>
            </Stack>
          </Grid2>

          <Grid2 size={6}>
            <Stack direction={'row'} alignItems={'center'}>
              <Typography component='span' className={cx('key')}>
                {t('schedule_detail_item_label_timezone')}
              </Typography>

              <Typography component='span' className={cx('value')}>
                {timeZone}
              </Typography>
            </Stack>
          </Grid2>

          {data?.businessType !== BusinessTypeEnum.EVENT && (
            <Grid2 size={6}>
              <Stack direction={'row'} alignItems={'center'}>
                <Typography component='span' className={cx('key')}>
                  {t('schedule_detail_item_label_customer')}
                </Typography>

                {scheduleDetails?.customer && (
                  <Stack direction={'row'} alignItems={'center'} gap={'10px'}>
                    <Avatar
                      className={cx('avatar')}
                      src={''}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                    >
                      <Typography className={cx('avatarText')}>
                        {getInitials({ ...scheduleDetails?.customer })}
                      </Typography>
                    </Avatar>

                    <Typography className={cx('avatarUserName')}>
                      {getFullName({ ...scheduleDetails?.customer })}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Grid2>
          )}

          <Grid2 size={6}>
            <Stack direction={'row'} alignItems={'center'}>
              <Typography component='span' className={cx('key')}>
                {t('schedule_detail_item_label_assignee')}
              </Typography>

              <BaseAvatarGroup avatars={scheduleDetails?.assignees || []} borderColor='#FFF' max={5} />
            </Stack>
          </Grid2>

          {data?.businessType === BusinessTypeEnum.EVENT && (
            <Grid2 size={12}>
              <Stack direction={'row'} alignItems={'center'}>
                <Typography component='span' className={cx('key')}>
                  {t('schedule_detail_item_label_task_note')}
                </Typography>

                <Typography component='span' className={cx('value')}>
                  {scheduleDetails?.event?.note || EMPTY_STRING}
                </Typography>
              </Stack>
            </Grid2>
          )}

          <Grid2 size={12}>
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
              <Box component={'div'} sx={{ width: '40%' }}>
                <IconButton color='error' onClick={handleShowConfirmDelete}>
                  <img
                    src={icons.activityIconDelete}
                    alt={t('common_alt_img')}
                    className={cx('deleteIcon')}
                  />
                </IconButton>
              </Box>

              <Stack direction={'row'} alignItems={'center'} gap={'8px'}>
                <Button variant='contained' onClick={handleShowEditSchedule}>
                  {t('schedule_detail_item_btn_edit')}
                </Button>

                {data?.businessType !== BusinessTypeEnum.EVENT && (
                  <Button variant='contained' onClick={handleShowDetail}>
                    {t('schedule_detail_item_btn_view_detail')}
                  </Button>
                )}
              </Stack>
            </Stack>
          </Grid2>
        </Grid2>
      )}

      {scheduleDetails?.businessType === BusinessTypeEnum.TASK && (
        <Grid2 container rowSpacing={'24px'} columnSpacing={'24px'}>
          <Grid2 size={6}>
            <Stack direction={'row'} alignItems={'center'}>
              <Typography component='span' className={cx('key')}>
                {t('schedule_detail_item_label_task_name')}
              </Typography>

              <Typography component='span' className={cx('value')}>
                {scheduleDetails?.task?.title || EMPTY_STRING}
              </Typography>
            </Stack>
          </Grid2>

          <Grid2 size={4}>
            <Stack direction={'row'} alignItems={'center'}>
              <Typography component='span' className={cx('key')}>
                {t('schedule_detail_item_label_due_date')}
              </Typography>

              <Typography component='span' className={cx('value')}>
                {scheduleDetails?.task?.dueDate
                  ? formatDate(scheduleDetails?.task?.dueDate, DateFormatEnum.DD_MMM_YYYY)
                  : EMPTY_STRING}
              </Typography>
            </Stack>
          </Grid2>

          <Grid2 size={12}>
            <Stack direction={'row'} alignItems={'center'}>
              <Typography component='span' className={cx('key')}>
                {t('schedule_detail_item_label_assignee')}
              </Typography>

              {scheduleDetails?.assignees && scheduleDetails?.assignees?.length > DEFAULT_NUMBER_ZERO && (
                <Stack direction={'row'} alignItems={'center'} gap={'10px'}>
                  <Avatar
                    className={cx('avatar')}
                    src={scheduleDetails?.assignees?.[DEFAULT_NUMBER_ZERO]?.avatarImage}
                    alt={t('common_alt_img', { ns: 'common/base' })}
                  >
                    <Typography className={cx('avatarText')}>
                      {getInitials({
                        firstName: scheduleDetails?.assignees?.[DEFAULT_NUMBER_ZERO]?.firstName,
                        lastName: scheduleDetails?.assignees?.[DEFAULT_NUMBER_ZERO]?.lastName,
                      })}
                    </Typography>
                  </Avatar>

                  <Typography className={cx('avatarUserName')}>
                    {getFullName({
                      firstName: scheduleDetails?.assignees?.[DEFAULT_NUMBER_ZERO]?.firstName,
                      lastName: scheduleDetails?.assignees?.[DEFAULT_NUMBER_ZERO]?.lastName,
                    })}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Grid2>

          <Grid2 size={12}>
            <Stack direction={'row'} alignItems={'center'}>
              <Typography component='span' className={cx('key')}>
                {t('schedule_detail_item_label_description')}
              </Typography>

              <Typography component='span' className={cx('value')}>
                {scheduleDetails?.task?.description || EMPTY_STRING}
              </Typography>
            </Stack>
          </Grid2>
        </Grid2>
      )}

      {isLoading && <Loading />}
    </Box>
  );
};

export default ScheduleDetailSummary;
